// Thin ExcelJS wrappers for bulk-upload flows.
//
// Two write helpers:
//   writeSheet(cfg)              — single-sheet workbook with inline-list and
//                                  numeric data validation. Returns a Blob.
//   writeSheetWithReferences(cfg) — two-sheet workbook: primary data sheet
//                                   + a hidden "_System" reference sheet
//                                   holding the lookup lists dropdowns point
//                                   at. Use this when any list is long enough
//                                   that an inline validation formula would
//                                   overflow Excel's 255-char cap.
//
// One read helper:
//   readFirstSheetAsRows(file)   — returns the first worksheet as a 2D array
//                                  of string cell values (blank-trimmed).
//
// Cell values are ALWAYS coerced to strings on read. CSVs used to lose
// leading zeros and mangle IDs when Excel opened them; XLSX preserves what
// the user typed, and downstream parsers already handle string→number.
//
// Lazy loading: exceljs is a ~1MB module. We dynamic-import it so the chunk
// only lands when a bulk sheet actually opens — webpack code-splits
// automatically. The import promise is memoised so repeated calls in the
// same session resolve immediately after the first load.

import type ExcelJS from 'exceljs';

export type XlsxColumnValidation =
  | { kind: 'list'; values: string[] }
  | { kind: 'decimal-min'; min: number }
  | { kind: 'decimal-range'; min: number; max: number }
  | { kind: 'integer-min'; min: number };

export type XlsxColumnSpec = {
  header: string;
  /** Column width in Excel units. Defaults to header length + 4. */
  width?: number;
  /** If provided, applied to every data-row cell in this column. */
  validation?: XlsxColumnValidation;
};

export type WriteSheetConfig = {
  sheetName: string;
  columns: XlsxColumnSpec[];
  /** Rows are 2D — each inner array is one row, aligned to columns. */
  rows: (string | number | null | undefined)[][];
  /** How many trailing empty rows to leave dropdown-validated for the user
   *  to fill. Default 200; caller can bump if the template is meant to
   *  grow much larger. */
  emptyRowsForValidation?: number;
};

export type ReferenceList = { name: string; values: string[] };

export type WriteSheetWithReferencesConfig = WriteSheetConfig & {
  references: ReferenceList[];
};

let excelJsPromise: Promise<typeof ExcelJS> | null = null;

function loadExcelJs(): Promise<typeof ExcelJS> {
  if (!excelJsPromise) {
    excelJsPromise = import('exceljs').then((mod) => {
      // exceljs ships both CJS and ESM entry points. In some bundler
      // configs the default export is the module itself, in others it's
      // wrapped in { default: … }. Handle both so we never accidentally
      // call `new (undefined)()`.
      const anyMod = mod as unknown as { default?: typeof ExcelJS };
      return (anyMod.default ?? (mod as unknown as typeof ExcelJS));
    });
  }
  return excelJsPromise;
}

/** Build a single-sheet workbook and return a Blob suitable for download. */
export async function writeSheet(cfg: WriteSheetConfig): Promise<Blob> {
  const XLSX = await loadExcelJs();
  const wb = new XLSX.Workbook();
  const ws = wb.addWorksheet(cfg.sheetName);
  applyColumnsAndRows(ws, cfg);
  const buf = await wb.xlsx.writeBuffer();
  return new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

/** Two-sheet workbook: primary data sheet + a hidden "_System" reference
 *  sheet that holds the source lists dropdowns point at. Use when any
 *  list would overflow Excel's 255-char inline validation formula cap. */
export async function writeSheetWithReferences(
  cfg: WriteSheetWithReferencesConfig,
): Promise<Blob> {
  const XLSX = await loadExcelJs();
  const wb = new XLSX.Workbook();

  // IMPORTANT: primary sheet MUST be added FIRST so it's at index 0.
  // Apple Numbers doesn't honor Excel's `activeTab` view preference OR
  // `state: 'hidden'` on a sheet — it always opens on sheet index 0 and
  // shows every sheet. Excel and Google Sheets honor both correctly;
  // putting the data sheet first is the workaround that makes the user
  // land on the right sheet in every viewer.
  const ws = wb.addWorksheet(cfg.sheetName);

  // state: 'hidden' (not 'veryHidden') because Google Sheets can fail to
  // resolve cross-sheet data validation ranges when the source sheet is
  // veryHidden — dropdowns render as empty. 'hidden' works in every
  // viewer we care about (Excel, Google Sheets, LibreOffice). Numbers
  // ignores both, so it doesn't matter which we pick for it.
  const refSheetName = '_System';
  const ref = wb.addWorksheet(refSheetName, { state: 'hidden' });
  ref.getRow(1).values = ['This sheet is auto-generated. Do not edit.'];
  ref.getRow(1).font = { italic: true, color: { argb: 'FF888888' } };

  const rangeByListName = new Map<string, string>();
  cfg.references.forEach((list, idx) => {
    const colLetter = columnIndexToLetter(idx + 1);
    // Row 1: info banner. Row 2: list header. Rows 3+: values.
    ref.getCell(`${colLetter}2`).value = list.name;
    ref.getCell(`${colLetter}2`).font = { bold: true };
    list.values.forEach((v, i) => {
      ref.getCell(`${colLetter}${i + 3}`).value = v;
    });
    ref.getColumn(idx + 1).width = Math.max(list.name.length + 2, 12);
    const range = `'${refSheetName}'!$${colLetter}$3:$${colLetter}$${list.values.length + 2}`;
    rangeByListName.set(list.name, range);
  });

  applyColumnsAndRows(ws, cfg, rangeByListName);

  const buf = await wb.xlsx.writeBuffer();
  return new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

function applyColumnsAndRows(
  ws: ExcelJS.Worksheet,
  cfg: WriteSheetConfig,
  externalRanges?: Map<string, string>,
) {
  ws.columns = cfg.columns.map((c) => ({
    header: c.header,
    key: c.header,
    width: c.width ?? Math.max(c.header.length + 4, 12),
  }));
  ws.getRow(1).font = { bold: true };

  for (const r of cfg.rows) {
    ws.addRow(r.map((v) => (v == null ? '' : v)));
  }

  // Per-column data validation applied across every data row + trailing
  // empty rows so the user can extend the sheet and still get the dropdown.
  const totalDataRows = cfg.rows.length + (cfg.emptyRowsForValidation ?? 200);
  cfg.columns.forEach((c, colIdx) => {
    if (!c.validation) return;
    const colLetter = columnIndexToLetter(colIdx + 1);
    for (let rowIdx = 2; rowIdx <= totalDataRows + 1; rowIdx++) {
      ws.getCell(`${colLetter}${rowIdx}`).dataValidation = validationFor(
        c.validation,
        externalRanges,
      );
    }
  });
}

function validationFor(
  v: XlsxColumnValidation,
  externalRanges?: Map<string, string>,
): ExcelJS.DataValidation {
  if (v.kind === 'list') {
    // Sentinel "@ref:<name>" means "point at the hidden Reference sheet
    // range named <name>" — used when the list is too long to inline.
    if (v.values.length === 1 && v.values[0].startsWith('@ref:')) {
      const listName = v.values[0].slice('@ref:'.length);
      const range = externalRanges?.get(listName);
      if (!range) {
        throw new Error(`xlsx: data validation references unknown list "${listName}"`);
      }
      return { type: 'list', allowBlank: true, formulae: [`=${range}`] };
    }
    return {
      type: 'list',
      allowBlank: true,
      // ExcelJS wants an inline list as a single quoted, comma-separated
      // string wrapped in double quotes.
      formulae: [`"${v.values.join(',')}"`],
    };
  }
  if (v.kind === 'decimal-range') {
    return {
      type: 'decimal',
      operator: 'between',
      formulae: [v.min, v.max],
      allowBlank: true,
      showErrorMessage: true,
      errorTitle: 'Invalid value',
      error: `Value must be a number from ${v.min} to ${v.max}`,
    };
  }
  if (v.kind === 'decimal-min') {
    return {
      type: 'decimal',
      operator: 'greaterThanOrEqual',
      formulae: [v.min],
      allowBlank: true,
      showErrorMessage: true,
      errorTitle: 'Invalid value',
      error: `Value must be a number >= ${v.min}`,
    };
  }
  return {
    type: 'whole',
    operator: 'greaterThanOrEqual',
    formulae: [v.min],
    allowBlank: true,
    showErrorMessage: true,
    errorTitle: 'Invalid value',
    error: `Value must be a whole number >= ${v.min}`,
  };
}

function columnIndexToLetter(n: number): string {
  // 1 -> A, 2 -> B, ..., 27 -> AA.
  let s = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    s = String.fromCharCode(65 + rem) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

/** Trigger a browser download of an XLSX blob. */
export function downloadXlsxBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke on next tick — some browsers (Safari) need the URL to still be
  // alive when the download initiates.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** Read the first worksheet from an uploaded XLSX. Returns a 2D array of
 *  string cell values (blank cells are empty strings; trailing fully-empty
 *  rows are trimmed). */
export async function readFirstSheetAsRows(file: File): Promise<string[][]> {
  const XLSX = await loadExcelJs();
  const wb = new XLSX.Workbook();
  await wb.xlsx.load(await file.arrayBuffer());
  const ws = wb.worksheets[0];
  if (!ws) return [];
  const out: string[][] = [];
  const width = ws.columnCount;
  ws.eachRow({ includeEmpty: false }, (row) => {
    const cells: string[] = [];
    for (let c = 1; c <= width; c++) {
      cells.push(cellToString(row.getCell(c).value));
    }
    out.push(cells);
  });
  while (out.length > 0 && out[out.length - 1].every((c) => c === '')) {
    out.pop();
  }
  return out;
}

function cellToString(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v.trim();
  if (typeof v === 'number') return String(v);
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (v instanceof Date) return v.toISOString();
  if (typeof v === 'object') {
    // ExcelJS wraps formulas / rich text — pull `.text` / `.result`.
    const obj = v as { text?: unknown; result?: unknown; richText?: { text?: string }[] };
    if (typeof obj.result === 'string' || typeof obj.result === 'number') {
      return String(obj.result);
    }
    if (typeof obj.text === 'string') return obj.text.trim();
    if (Array.isArray(obj.richText)) {
      return obj.richText.map((r) => r.text ?? '').join('').trim();
    }
  }
  return String(v);
}
